/**
 * PaperFormHeader — three-block header for paper-form surfaces:
 *   Left:   form ID overline + Title + subtitle
 *   Right:  status pill + RFI/Job/Doc number + date
 *
 * Pattern extracted from RfiForm.jsx redesign (commit 3c68067).
 */

import React from "react";
import { Box, Stack, Typography, Chip, Divider } from "@mui/material";
import { DescriptionOutlined } from "@mui/icons-material";
import { STATUS_COLOR, prettyStatus } from "./styles";

export default function PaperFormHeader({
  formId,        // e.g. "SFM · Form F-1.6"
  title,         // e.g. "Request for Information"
  subtitle,      // e.g. "AISC §1.6.0 detailing RFI · per D-033 §3.1 paper-form layout"
  status,        // e.g. "DRAFT"
  number,        // e.g. "RFI #2026-014" — rendered in mono
  numberLabel = "Number",
  date,          // string — pre-formatted by caller
  dateLabel = "Date",
  icon,          // optional icon override (default: DescriptionOutlined)
  showDivider = true,
}) {
  const statusStyle = STATUS_COLOR[status] || STATUS_COLOR.DRAFT;
  const Icon = icon || DescriptionOutlined;

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "flex-start" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          {formId && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Icon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography
                variant="overline"
                sx={{ letterSpacing: "0.12em", color: "text.secondary", fontWeight: 700 }}
              >
                {formId}
              </Typography>
            </Stack>
          )}
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.15 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {(status || number || date) && (
          <Stack alignItems={{ xs: "flex-start", md: "flex-end" }} spacing={1.5}>
            {status && (
              <Chip
                label={prettyStatus(status)}
                size="small"
                sx={{
                  bgcolor: statusStyle.bg,
                  color: statusStyle.color,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                }}
              />
            )}
            {number && (
              <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontWeight: 600,
                  }}
                >
                  {numberLabel}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontWeight: 500,
                  }}
                >
                  {number}
                </Typography>
              </Box>
            )}
            {date && (
              <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontWeight: 600,
                  }}
                >
                  {dateLabel}
                </Typography>
                <Typography variant="body2">{date}</Typography>
              </Box>
            )}
          </Stack>
        )}
      </Stack>
      {showDivider && <Divider sx={{ mb: 4 }} />}
    </>
  );
}
